
import { useEffect, useRef } from "react";
import { Award, Clock, Users, Target, Handshake, CheckCircle2 } from "lucide-react";
import teamPhoto from "@/assets/team-photo.jpg";

export const About = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    const elements = sectionRef.current?.querySelectorAll('.animate-on-scroll, .animate-on-scroll-left, .animate-on-scroll-right');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const advantages = [
    "Senior professionals on every project",
    "Architect-level guidance for every engagement",
    "Deep expertise in ERP, CRM, and Supply Chain",
    "Quick ramp-up with minimal onboarding"
  ];

  const achievements = [
    { icon: Users, title: "Senior Team", description: "Experienced professionals with real-world expertise" },
    { icon: Award, title: "Proven Track Record", description: "Successful implementations across industries" },
    { icon: Clock, title: "Efficient Delivery", description: "Streamlined processes for faster time-to-value" },
    { icon: Target, title: "Strategic Impact", description: "Solutions focused on business growth and efficiency" },
    { icon: Handshake, title: "Collaborative Approach", description: "Working as an extension of your team" }
  ];

  return (
    <section ref={sectionRef} id="about" className="py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-background" />
      <div className="absolute inset-0 mesh-gradient-1 opacity-30" />

      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-amber-500/5 rounded-full blur-[150px]" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-cyan-500/5 rounded-full blur-[120px]" />

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 grid-pattern opacity-20" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Editorial Layout - Asymmetric Grid */}
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">

          {/* Left Column - Main Content */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            {/* Section Label */}
            <div className="animate-on-scroll-left">
              <span className="label-mono text-amber-400 tracking-widest">
                About FortifyTek
              </span>
            </div>

            {/* Main Heading */}
            <h2 className="mt-6 display-lg text-white animate-on-scroll-left stagger-1">
              Decades of
              <span className="text-gradient-gold block">Mastery</span>
            </h2>

            {/* Lead Paragraph */}
            <p className="mt-8 text-2xl text-white font-medium leading-relaxed animate-on-scroll-left stagger-2">
              Founded by industry veterans who believe that true expertise cannot be faked.
            </p>

            {/* Body Content */}
            <div className="mt-8 space-y-6 animate-on-scroll-left stagger-3">
              <p className="text-lg text-zinc-400 leading-relaxed">
                At FortifyTek, we prioritize quality:
                <span className="text-amber-400 font-semibold"> Our team consists of senior professionals</span> with hands-on experience managing and building critical business applications. We focus on delivering real value through expertise and dedication.
              </p>

              <p className="text-lg text-zinc-400 leading-relaxed">
                We specialize in complex ERP implementations, eCommerce solutions, and intricate system integrations. When you need reliable technology partners, we're here to help.
              </p>
            </div>

            {/* Team Photo with Overlay */}
            <div className="mt-12 relative animate-on-scroll-left stagger-4">
              <div className="relative rounded-2xl overflow-hidden group">
                <img
                  src={teamPhoto}
                  alt="FortifyTek Expert Team"
                  className="w-full h-72 lg:h-80 object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                {/* Overlay Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center">
                      <Users className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">Experienced Team</p>
                      <p className="text-sm text-zinc-400">Senior professionals dedicated to your success</p>
                    </div>
                  </div>
                </div>

                {/* Corner Accent */}
                <div className="absolute top-4 right-4 w-12 h-12 border-t border-r border-amber-500/30" />
              </div>
            </div>

            {/* The FortifyTek Advantage Box */}
            <div className="mt-12 card-featured p-8 animate-on-scroll-left stagger-5">
              <div className="flex items-start gap-4">
                <div className="w-1 h-full bg-gradient-to-b from-amber-500 to-transparent rounded-full" />
                <div>
                  <h3 className="text-2xl font-bold text-white mb-4">The FortifyTek Advantage</h3>
                  <p className="text-zinc-300 leading-relaxed mb-6">
                    <span className="text-white font-semibold">Experience matters.</span> Our team anticipates challenges and addresses them proactively, helping you save time and resources. We bring practical knowledge and proven approaches to your project.
                  </p>

                  {/* Advantages List */}
                  <ul className="space-y-3">
                    {advantages.map((advantage, index) => (
                      <li key={index} className="flex items-center gap-3 text-zinc-400">
                        <CheckCircle2 className="w-5 h-5 text-amber-400 flex-shrink-0" />
                        <span>{advantage}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Achievement Cards */}
          <div className="lg:col-span-5 order-1 lg:order-2 lg:sticky lg:top-32">
            <div className="space-y-4">
              {achievements.map((achievement, index) => (
                <div
                  key={index}
                  className={`animate-on-scroll-right stagger-${index + 1} group card-editorial p-6 hover:border-amber-500/30`}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-500/5 border border-amber-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <achievement.icon className="w-6 h-6 text-amber-400" />
                    </div>

                    {/* Content */}
                    <div>
                      <h4 className="text-lg font-bold text-white group-hover:text-amber-400 transition-colors">
                        {achievement.title}
                      </h4>
                      <p className="mt-1 text-sm text-zinc-500 leading-relaxed">
                        {achievement.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Floating CTA Card */}
            <div className="mt-8 glass-panel-warm rounded-2xl p-6 animate-on-scroll-right stagger-6">
              <div className="text-center">
                <p className="text-lg font-semibold text-white mb-2">Ready to get started?</p>
                <p className="text-sm text-zinc-400 mb-4">Let's discuss how we can help your business.</p>
                <a href="/contact" className="inline-flex items-center gap-2 text-amber-400 font-semibold hover:text-amber-300 transition-colors">
                  Contact Us
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
